import { useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import axios from "axios";
import { useAuth } from "../context/AuthContext";
import DashboardLayout from "../components/DashboardLayout";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { 
  FileText, 
  Loader2, 
  CheckCircle2, 
  AlertTriangle,
  Sparkles,
  ArrowLeft,
  Zap,
  ListChecks,
  AlertCircle,
  ClipboardList,
  Crown,
  Download,
  Lock
} from "lucide-react";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import UploadForm from "./synthese/UploadForm";
import DetailedSections from "./synthese/DetailedSections";
import { usePdfExport } from "./synthese/usePdfExport";
import { formatValue, hasValue } from "./synthese/utils";
import { trackAnalysisStart, trackAnalysisCompleted, trackAnalysisFailed } from "../utils/analytics";

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function SyntheseMarchesPage() {
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  const resultRef = useRef(null);
  const { user } = useAuth();
  
  const [file, setFile] = useState(null);
  const [fileName, setFileName] = useState("");
  const [fileData, setFileData] = useState(null);
  const [domain, setDomain] = useState("");
  const [documentType, setDocumentType] = useState("");
  const [mode, setMode] = useState("standard");
  const [customPrompt, setCustomPrompt] = useState("");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");

  // Plan check: paid tier OR active trial → can download PDF in detailed mode
  const isPaidUser =
    !!user &&
    (["pro", "enterprise", "premium", "starter"].includes(user.subscription_tier) ||
      user.trial_active === true);

  // PDF export logic (hook owns canvas → jsPDF → save() workflow)
  const { generatingPdf, downloadPdf } = usePdfExport({
    resultRef,
    fileName,
    domain,
    documentType,
  });

  const handleDownloadPdf = async () => {
    if (!result) {
      toast.error("Aucune analyse à exporter");
      return;
    }
    if (!isPaidUser) {
      toast.error("Le téléchargement PDF est réservé aux abonnements payants");
      navigate("/tarifs-synthese");
      return;
    }
    await downloadPdf();
  };

  const handleFileChange = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (!f.name.toLowerCase().endsWith(".pdf")) {
      toast.error("Seuls les fichiers PDF sont acceptés");
      return;
    }
    setFile(f);
    setFileName(f.name);
    const reader = new FileReader();
    reader.onload = (ev) => {
      const base64 = ev.target.result.split(",")[1];
      setFileData(base64);
    };
    reader.readAsDataURL(f);
    toast.success("Document chargé avec succès");
  };

  const handleDrop = (e) => {
    e.preventDefault();
    const f = e.dataTransfer.files?.[0];
    if (!f) return;
    if (!f.name.toLowerCase().endsWith(".pdf")) {
      toast.error("Seuls les fichiers PDF sont acceptés");
      return;
    }
    setFile(f);
    setFileName(f.name);
    const reader = new FileReader();
    reader.onload = (ev) => {
      const base64 = ev.target.result.split(",")[1];
      setFileData(base64);
    };
    reader.readAsDataURL(f);
    toast.success("Document chargé avec succès");
  };

  const handleSubmit = async () => {
    setError("");
    setResult(null);

    if (!fileData) {
      toast.error("Veuillez déposer un document PDF");
      return;
    }
    if (!domain) {
      toast.error("Sélectionnez un domaine");
      return;
    }
    if (!documentType) {
      toast.error("Sélectionnez un type de document");
      return;
    }

    const token = localStorage.getItem("token");
    if (!token) {
      toast.error("Vous devez être connecté pour utiliser l'analyse IA");
      return;
    }

    setLoading(true);
    const analysisStartedAt = Date.now();
    trackAnalysisStart({ domain, documentType, mode });
    try {
      const response = await axios.post(
        `${API_URL}/api/analyze-document`,
        {
          file_data: fileData,
          file_name: fileName,
          domain: domain,
          document_type: documentType,
          mode: mode,
          custom_prompt: customPrompt
        },
        {
          headers: {
            "Content-Type": "application/json",
            "Authorization": `Bearer ${token}`
          },
          timeout: 180000,  // 3 minutes for large PDFs
          maxContentLength: Infinity,
          maxBodyLength: Infinity
        }
      );

      if (response.data.success) {
        setResult(response.data.result);
        trackAnalysisCompleted({
          domain,
          documentType,
          mode,
          durationMs: Date.now() - analysisStartedAt,
        });
        toast.success("Analyse terminée !");
      } else {
        throw new Error(response.data.detail || "Erreur lors de l'analyse");
      }
    } catch (e) {
      // Better error message for network/timeout errors
      let errorMsg;
      let reason = "unknown";
      if (e.code === 'ECONNABORTED' || e.message?.includes('timeout')) {
        errorMsg = "L'analyse a dépassé le délai imparti. Le document est peut-être trop volumineux. Essayez le mode 'Courte' ou un document plus petit.";
        reason = "timeout";
      } else if (e.message === 'Network Error') {
        errorMsg = "Erreur réseau lors de l'analyse. Le document est peut-être trop volumineux ou le serveur est temporairement indisponible. Réessayez dans quelques secondes.";
        reason = "network";
      } else {
        errorMsg = e.response?.data?.detail || e.message || "Erreur lors de l'analyse";
        reason = e.response?.status ? `http_${e.response.status}` : "server";
      }
      trackAnalysisFailed({ domain, documentType, mode, reason });
      setError(errorMsg);
      toast.error(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFile(null);
    setFileName("");
    setFileData(null);
    setDomain("");
    setDocumentType("");
    setMode("standard");
    setCustomPrompt("");
    setResult(null);
    setError("");
  };

  return (
    <DashboardLayout>
      <div className="space-y-6">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <Button
            variant="ghost"
            onClick={() => navigate("/dashboard")}
            className="mb-4 text-slate-600 hover:text-slate-900"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Retour au dashboard
          </Button>
          
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-violet-500 to-indigo-600 flex items-center justify-center">
                <Sparkles className="w-6 h-6 text-white" />
              </div>
              <div>
                <h1 className="font-display text-2xl font-bold text-slate-900">
                  Synthèse de Document IA
                </h1>
                <p className="text-slate-600">
                  Analysez vos documents de marchés publics avec l'intelligence artificielle
                </p>
              </div>
            </div>
            <Link to="/tarifs-synthese">
              <Button variant="outline" className="border-violet-300 text-violet-700 hover:bg-violet-50">
                <Crown className="w-4 h-4 mr-2" />
                Voir les plans
              </Button>
            </Link>
          </div>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Left Column - Form (extracted to ./synthese/UploadForm.js) */}
          <UploadForm
            fileInputRef={fileInputRef}
            fileData={fileData}
            fileName={fileName}
            domain={domain}
            setDomain={setDomain}
            documentType={documentType}
            setDocumentType={setDocumentType}
            mode={mode}
            setMode={setMode}
            customPrompt={customPrompt}
            setCustomPrompt={setCustomPrompt}
            loading={loading}
            onFileChange={handleFileChange}
            onDrop={handleDrop}
            onSubmit={handleSubmit}
          />

          {/* Right Column - Results */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
          >
            <Card className="h-full min-h-[600px]">
              <CardHeader className="border-b">
                <CardTitle className="text-lg flex items-center gap-2">
                  <ListChecks className="w-5 h-5 text-violet-600" />
                  Résultat de l'analyse
                </CardTitle>
              </CardHeader>
              <CardContent className="p-6">
                {error && (
                  <div className="flex items-start gap-3 p-4 bg-red-50 border border-red-200 rounded-xl text-red-700">
                    <AlertCircle className="w-5 h-5 mt-0.5 flex-shrink-0" />
                    <p>{error}</p>
                  </div>
                )}

                {!result && !error && !loading && (
                  <div className="flex flex-col items-center justify-center h-[400px] text-slate-400">
                    <FileText className="w-16 h-16 mb-4 opacity-50" />
                    <p className="text-lg font-medium">Prêt à analyser</p>
                    <p className="text-sm">Déposez un PDF et lancez l'analyse</p>
                  </div>
                )}

                {loading && (
                  <div className="flex flex-col items-center justify-center h-[400px]">
                    <div className="relative">
                      <div className="w-20 h-20 rounded-full border-4 border-violet-200 border-t-violet-600 animate-spin" />
                      <Sparkles className="w-8 h-8 text-violet-600 absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2" />
                    </div>
                    <p className="mt-6 text-lg font-medium text-slate-700">Analyse en cours...</p>
                    <p className="text-sm text-slate-500">L'IA examine votre document</p>
                  </div>
                )}

                {result && (
                  <div ref={resultRef} className="space-y-6 overflow-y-auto max-h-[600px]" data-testid="synthese-result-panel">
                    {/* Show message if result is empty */}
                    {Object.keys(result).length === 0 && (
                      <div className="text-center py-8 bg-amber-50 rounded-xl border border-amber-200">
                        <AlertTriangle className="w-12 h-12 text-amber-500 mx-auto mb-4" />
                        <h3 className="font-semibold text-amber-700 mb-2">Aucune information extraite</h3>
                        <p className="text-amber-600 text-sm max-w-md mx-auto">
                          Le document n'a pas pu être analysé correctement. Vérifiez que le PDF contient du texte lisible (non scanné) et réessayez.
                        </p>
                      </div>
                    )}
                    
                    {/* RC Result - Structured Fields */}
                    {documentType === "rc" && Object.keys(result).length > 0 && (
                      <>
                        {/* Header Info */}
                        {(hasValue(result.id) || hasValue(result.titre)) && (
                          <div className="bg-violet-50 p-4 rounded-xl border border-violet-200">
                            {hasValue(result.id) && (
                              <Badge className="bg-violet-100 text-violet-700 mb-2">{result.id}</Badge>
                            )}
                            {hasValue(result.titre) && (
                              <h3 className="font-semibold text-slate-900 text-lg">{result.titre}</h3>
                            )}
                          </div>
                        )}

                        {/* Acheteur */}
                        {hasValue(result.acheteur) && (
                          <div>
                            <h3 className="font-semibold text-slate-900 mb-2 flex items-center gap-2">
                              <div className="w-2 h-2 rounded-full bg-blue-500" />
                              Acheteur
                            </h3>
                            <p className="text-slate-600 bg-slate-50 p-3 rounded-lg whitespace-pre-line">{formatValue(result.acheteur)}</p>
                          </div>
                        )}

                        {/* Date limite */}
                        {hasValue(result.limite) && (
                          <div className="bg-red-50 p-4 rounded-xl border border-red-200">
                            <h3 className="font-semibold text-red-700 mb-1 flex items-center gap-2">
                              <AlertTriangle className="w-4 h-4" />
                              Date limite
                            </h3>
                            <p className="text-red-800 font-medium">{formatValue(result.limite)}</p>
                          </div>
                        )}

                        {/* Grid Info */}
                        <div className="grid grid-cols-2 gap-4">
                          {hasValue(result.volume) && (
                            <div className="bg-slate-50 p-3 rounded-lg">
                              <span className="text-xs text-slate-500">Montant estimé</span>
                              <p className="font-medium text-slate-900">{formatValue(result.volume)}</p>
                            </div>
                          )}
                          {hasValue(result.duree_marche || result.period) && (
                            <div className="bg-slate-50 p-3 rounded-lg">
                              <span className="text-xs text-slate-500">Durée</span>
                              <p className="font-medium text-slate-900">{formatValue(result.duree_marche || result.period)}</p>
                            </div>
                          )}
                          {hasValue(result.cibleZoneGeo) && (
                            <div className="bg-slate-50 p-3 rounded-lg">
                              <span className="text-xs text-slate-500">Zone géographique</span>
                              <p className="font-medium text-slate-900">{formatValue(result.cibleZoneGeo)}</p>
                            </div>
                          )}
                          {hasValue(result.nature_produits) && (
                            <div className="bg-slate-50 p-3 rounded-lg">
                              <span className="text-xs text-slate-500">Nature</span>
                              <p className="font-medium text-slate-900">{formatValue(result.nature_produits)}</p>
                            </div>
                          )}
                        </div>

                        {/* Contenu */}
                        {hasValue(result.contenu) && (
                          <div>
                            <h3 className="font-semibold text-slate-900 mb-2 flex items-center gap-2">
                              <div className="w-2 h-2 rounded-full bg-violet-500" />
                              Description
                            </h3>
                            <p className="text-slate-600 bg-slate-50 p-3 rounded-lg">{formatValue(result.contenu)}</p>
                          </div>
                        )}

                        {/* Descriptif / Allotissement */}
                        {hasValue(result.descriptif) && (
                          <div>
                            <h3 className="font-semibold text-slate-900 mb-2 flex items-center gap-2">
                              <div className="w-2 h-2 rounded-full bg-green-500" />
                              Allotissement & Descriptif
                            </h3>
                            <p className="text-slate-600 bg-slate-50 p-3 rounded-lg whitespace-pre-line">{formatValue(result.descriptif)}</p>
                          </div>
                        )}

                        {/* Critères */}
                        {hasValue(result.criteres) && (
                          <div>
                            <h3 className="font-semibold text-slate-900 mb-2 flex items-center gap-2">
                              <div className="w-2 h-2 rounded-full bg-amber-500" />
                              Critères d'attribution
                            </h3>
                            <p className="text-slate-600 bg-amber-50 p-3 rounded-lg border border-amber-200 whitespace-pre-line">{formatValue(result.criteres)}</p>
                          </div>
                        )}

                        {/* Visite obligatoire */}
                        {hasValue(result.visite_obligatoire) && result.visite_obligatoire.toString().toLowerCase() !== "non" && (
                          <div className="bg-orange-50 p-4 rounded-xl border border-orange-200">
                            <h3 className="font-semibold text-orange-700 mb-1">Visite obligatoire</h3>
                            <p className="text-orange-800">{formatValue(result.visite_obligatoire)}</p>
                          </div>
                        )}

                        {/* Attentes */}
                        {hasValue(result.description_attentes) && (
                          <div>
                            <h3 className="font-semibold text-slate-900 mb-2 flex items-center gap-2">
                              <div className="w-2 h-2 rounded-full bg-blue-500" />
                              Attentes spécifiques
                            </h3>
                            <p className="text-slate-600 bg-blue-50 p-3 rounded-lg border border-blue-200 whitespace-pre-line">{formatValue(result.description_attentes)}</p>
                          </div>
                        )}

                        {/* Avis */}
                        {hasValue(result.avis) && (
                          <div>
                            <h3 className="font-semibold text-slate-900 mb-2 flex items-center gap-2">
                              <div className="w-2 h-2 rounded-full bg-violet-500" />
                              Avis & Points de vigilance
                            </h3>
                            <p className="text-slate-600 bg-violet-50 p-3 rounded-lg border border-violet-200">{formatValue(result.avis)}</p>
                          </div>
                        )}

                        {/* Link DCE */}
                        {hasValue(result.linkDCE) && (
                          <div className="bg-green-50 p-4 rounded-xl border border-green-200">
                            <h3 className="font-semibold text-green-700 mb-1">Lien DCE</h3>
                            <p className="text-green-800 break-all">{formatValue(result.linkDCE)}</p>
                          </div>
                        )}
                      </>
                    )}

                    {/* CCAP Result - Enhanced for domain-specific fields */}
                    {documentType === "ccap" && Object.keys(result).length > 0 && (
                      <>
                        {/* Header Info */}
                        {(result.id || result.titre_operation || result.titre_mission || result.titre_marche) && (
                          <div className="bg-amber-50 p-4 rounded-xl border border-amber-200">
                            {hasValue(result.id) && (
                              <Badge className="bg-amber-100 text-amber-700 mb-2">{result.id}</Badge>
                            )}
                            <h3 className="font-semibold text-slate-900 text-lg">
                              {result.titre_operation || result.titre_mission || result.titre_marche}
                            </h3>
                            {hasValue(result.acheteur) && (
                              <p className="text-sm text-slate-600 mt-1 whitespace-pre-line">{result.acheteur}</p>
                            )}
                          </div>
                        )}

                        {/* Cadre juridique */}
                        {hasValue(result.cadre_juridique) && (
                          <div>
                            <h3 className="font-semibold text-slate-900 mb-2 flex items-center gap-2">
                              <div className="w-2 h-2 rounded-full bg-slate-500" />
                              Cadre juridique
                            </h3>
                            <p className="text-slate-600 bg-slate-50 p-3 rounded-lg whitespace-pre-line">{result.cadre_juridique}</p>
                          </div>
                        )}

                        {/* Forme du marché */}
                        {hasValue(result.forme_marche) && (
                          <div>
                            <h3 className="font-semibold text-slate-900 mb-2 flex items-center gap-2">
                              <div className="w-2 h-2 rounded-full bg-violet-500" />
                              Forme du marché
                            </h3>
                            <p className="text-slate-600 bg-violet-50 p-3 rounded-lg border border-violet-200">{result.forme_marche}</p>
                          </div>
                        )}

                        {/* Prix et révision */}
                        {(result.prix_et_revision || result.conditions_prix) && (
                          <div>
                            <h3 className="font-semibold text-slate-900 mb-2 flex items-center gap-2">
                              <div className="w-2 h-2 rounded-full bg-blue-500" />
                              Prix et révision
                            </h3>
                            <p className="text-slate-600 bg-blue-50 p-3 rounded-lg border border-blue-200 whitespace-pre-line">
                              {result.prix_et_revision || result.conditions_prix}
                            </p>
                          </div>
                        )}

                        {/* Grid financier */}
                        <div className="grid grid-cols-2 gap-4">
                          {(result.avance_et_acomptes || result.avance) && (
                            <div className="bg-green-50 p-3 rounded-lg border border-green-200">
                              <span className="text-xs text-green-600">Avance & Acomptes</span>
                              <p className="font-medium text-green-800 text-sm whitespace-pre-line">{result.avance_et_acomptes || result.avance}</p>
                            </div>
                          )}
                          {hasValue(result.retenue_garantie) && (
                            <div className="bg-slate-50 p-3 rounded-lg">
                              <span className="text-xs text-slate-500">Retenue de garantie</span>
                              <p className="font-medium text-slate-900 text-sm">{result.retenue_garantie}</p>
                            </div>
                          )}
                          {hasValue(result.delais_paiement) && (
                            <div className="bg-slate-50 p-3 rounded-lg">
                              <span className="text-xs text-slate-500">Délais de paiement</span>
                              <p className="font-medium text-slate-900 text-sm">{result.delais_paiement}</p>
                            </div>
                          )}
                          {hasValue(result.commande_et_approvisionnement) && (
                            <div className="bg-cyan-50 p-3 rounded-lg border border-cyan-200">
                              <span className="text-xs text-cyan-600">Commandes</span>
                              <p className="font-medium text-cyan-800 text-sm">{result.commande_et_approvisionnement}</p>
                            </div>
                          )}
                        </div>

                        {/* Délais d'exécution / livraison */}
                        {(result.delais_execution || result.delais_livraison || result.delais) && (
                          <div>
                            <h3 className="font-semibold text-slate-900 mb-2 flex items-center gap-2">
                              <div className="w-2 h-2 rounded-full bg-violet-500" />
                              Délais d'exécution
                            </h3>
                            <p className="text-slate-600 bg-slate-50 p-3 rounded-lg whitespace-pre-line">
                              {result.delais_execution || result.delais_livraison || result.delais}
                            </p>
                          </div>
                        )}

                        {/* Pénalités - Alerte */}
                        {hasValue(result.penalites) && (
                          <div className="bg-red-50 p-4 rounded-xl border border-red-200">
                            <h3 className="font-semibold text-red-700 mb-1 flex items-center gap-2">
                              <AlertTriangle className="w-4 h-4" />
                              Pénalités
                            </h3>
                            <p className="text-red-800 whitespace-pre-line">{result.penalites}</p>
                          </div>
                        )}

                        {/* Réception */}
                        {(result.reception_travaux || result.reception_livrables || result.reception_et_conformite) && (
                          <div>
                            <h3 className="font-semibold text-slate-900 mb-2 flex items-center gap-2">
                              <div className="w-2 h-2 rounded-full bg-emerald-500" />
                              Réception
                            </h3>
                            <p className="text-slate-600 bg-emerald-50 p-3 rounded-lg border border-emerald-200 whitespace-pre-line">
                              {result.reception_travaux || result.reception_livrables || result.reception_et_conformite}
                            </p>
                          </div>
                        )}

                        {/* Garanties (Travaux) */}
                        {hasValue(result.garanties) && (
                          <div>
                            <h3 className="font-semibold text-slate-900 mb-2 flex items-center gap-2">
                              <div className="w-2 h-2 rounded-full bg-indigo-500" />
                              Garanties
                            </h3>
                            <p className="text-slate-600 bg-indigo-50 p-3 rounded-lg border border-indigo-200 whitespace-pre-line">{result.garanties}</p>
                          </div>
                        )}

                        {/* Garantie SAV (Fournitures) */}
                        {hasValue(result.garantie_sav) && (
                          <div>
                            <h3 className="font-semibold text-slate-900 mb-2 flex items-center gap-2">
                              <div className="w-2 h-2 rounded-full bg-indigo-500" />
                              Garantie & SAV
                            </h3>
                            <p className="text-slate-600 bg-indigo-50 p-3 rounded-lg border border-indigo-200 whitespace-pre-line">{result.garantie_sav}</p>
                          </div>
                        )}

                        {/* Assurances */}
                        {hasValue(result.assurances) && (
                          <div>
                            <h3 className="font-semibold text-slate-900 mb-2 flex items-center gap-2">
                              <div className="w-2 h-2 rounded-full bg-blue-500" />
                              Assurances
                            </h3>
                            <p className="text-slate-600 bg-slate-50 p-3 rounded-lg whitespace-pre-line">{result.assurances}</p>
                          </div>
                        )}

                        {/* Sous-traitance */}
                        {hasValue(result.sous_traitance) && (
                          <div>
                            <h3 className="font-semibold text-slate-900 mb-2 flex items-center gap-2">
                              <div className="w-2 h-2 rounded-full bg-teal-500" />
                              Sous-traitance
                            </h3>
                            <p className="text-slate-600 bg-teal-50 p-3 rounded-lg border border-teal-200 whitespace-pre-line">{result.sous_traitance}</p>
                          </div>
                        )}

                        {/* Confidentialité / Données (Services) */}
                        {hasValue(result.confidentialite_donnees) && (
                          <div>
                            <h3 className="font-semibold text-slate-900 mb-2 flex items-center gap-2">
                              <div className="w-2 h-2 rounded-full bg-purple-500" />
                              Confidentialité & RGPD
                            </h3>
                            <p className="text-slate-600 bg-purple-50 p-3 rounded-lg border border-purple-200 whitespace-pre-line">{result.confidentialite_donnees}</p>
                          </div>
                        )}

                        {/* Propriété intellectuelle (Services) */}
                        {hasValue(result.propriete_intellectuelle) && (
                          <div>
                            <h3 className="font-semibold text-slate-900 mb-2 flex items-center gap-2">
                              <div className="w-2 h-2 rounded-full bg-pink-500" />
                              Propriété intellectuelle
                            </h3>
                            <p className="text-slate-600 bg-pink-50 p-3 rounded-lg border border-pink-200 whitespace-pre-line">{result.propriete_intellectuelle}</p>
                          </div>
                        )}

                        {/* Traçabilité (Fournitures) */}
                        {hasValue(result.tracabilite_conformite) && (
                          <div>
                            <h3 className="font-semibold text-slate-900 mb-2 flex items-center gap-2">
                              <div className="w-2 h-2 rounded-full bg-cyan-500" />
                              Traçabilité & Conformité
                            </h3>
                            <p className="text-slate-600 bg-cyan-50 p-3 rounded-lg border border-cyan-200 whitespace-pre-line">{result.tracabilite_conformite}</p>
                          </div>
                        )}

                        {/* Modifications du marché */}
                        {hasValue(result.modifications_marche) && (
                          <div>
                            <h3 className="font-semibold text-slate-900 mb-2 flex items-center gap-2">
                              <div className="w-2 h-2 rounded-full bg-slate-500" />
                              Modifications du marché
                            </h3>
                            <p className="text-slate-600 bg-slate-50 p-3 rounded-lg whitespace-pre-line">{result.modifications_marche}</p>
                          </div>
                        )}

                        {/* Résiliation */}
                        {hasValue(result.resiliation) && (
                          <div className="bg-amber-50 p-4 rounded-xl border border-amber-200">
                            <h3 className="font-semibold text-amber-700 mb-1">Conditions de résiliation</h3>
                            <p className="text-amber-800 whitespace-pre-line">{result.resiliation}</p>
                          </div>
                        )}

                        {/* Règlement des litiges */}
                        {(result.reglement_litiges || result.litiges) && (
                          <div>
                            <h3 className="font-semibold text-slate-900 mb-2 flex items-center gap-2">
                              <div className="w-2 h-2 rounded-full bg-slate-500" />
                              Règlement des litiges
                            </h3>
                            <p className="text-slate-600 bg-slate-50 p-3 rounded-lg">{result.reglement_litiges || result.litiges}</p>
                          </div>
                        )}

                        {/* Obligations sociales / environnementales */}
                        {hasValue(result.obligations_sociales_environnementales) && (
                          <div>
                            <h3 className="font-semibold text-slate-900 mb-2 flex items-center gap-2">
                              <div className="w-2 h-2 rounded-full bg-green-600" />
                              Obligations sociales & environnementales
                            </h3>
                            <p className="text-slate-600 bg-green-50 p-3 rounded-lg border border-green-200 whitespace-pre-line">{result.obligations_sociales_environnementales}</p>
                          </div>
                        )}

                        {/* Documents à fournir */}
                        {hasValue(result.documents_a_fournir) && (
                          <div>
                            <h3 className="font-semibold text-slate-900 mb-2 flex items-center gap-2">
                              <div className="w-2 h-2 rounded-full bg-blue-500" />
                              Documents à fournir
                            </h3>
                            <p className="text-slate-600 bg-blue-50 p-3 rounded-lg border border-blue-200 whitespace-pre-line">{result.documents_a_fournir}</p>
                          </div>
                        )}

                        {/* Points de vigilance */}
                        {hasValue(result.points_vigilance) && (
                          <div className="bg-red-50 p-4 rounded-xl border border-red-200">
                            <h3 className="font-semibold text-red-700 mb-2 flex items-center gap-2">
                              <AlertTriangle className="w-4 h-4" />
                              Points de vigilance
                            </h3>
                            <p className="text-red-800 whitespace-pre-line">{result.points_vigilance}</p>
                          </div>
                        )}

                        {/* Niveau de contrainte */}
                        {hasValue(result.niveau_contrainte_administrative) && (
                          <div className="bg-violet-50 p-4 rounded-xl border border-violet-200">
                            <h3 className="font-semibold text-violet-700 mb-1">Niveau de contrainte administrative</h3>
                            <p className="text-violet-800">{result.niveau_contrainte_administrative}</p>
                          </div>
                        )}

                        {/* Alertes - fallback pour ancien format */}
                        {hasValue(result.alertes) && (Array.isArray(result.alertes) ? result.alertes.length > 0 : result.alertes) && (
                          <div>
                            <h3 className="font-semibold text-slate-900 mb-2 flex items-center gap-2">
                              <div className="w-2 h-2 rounded-full bg-red-500" />
                              Alertes identifiées
                            </h3>
                            <ul className="space-y-2">
                              {(Array.isArray(result.alertes) ? result.alertes : [result.alertes]).map((alerte, i) => (
                                <li key={`item-${i}`} className="flex items-start gap-2 text-red-700 bg-red-50 p-3 rounded-lg">
                                  <AlertTriangle className="w-5 h-5 mt-0.5 flex-shrink-0" />
                                  <span>{alerte}</span>
                                </li>
                              ))}
                            </ul>
                          </div>
                        )}

                        {/* Actions recommandées */}
                        {hasValue(result.actions_recommandees) && (Array.isArray(result.actions_recommandees) ? result.actions_recommandees.length > 0 : result.actions_recommandees) && (
                          <div>
                            <h3 className="font-semibold text-slate-900 mb-2 flex items-center gap-2">
                              <div className="w-2 h-2 rounded-full bg-green-500" />
                              Actions recommandées
                            </h3>
                            <ul className="space-y-2">
                              {(Array.isArray(result.actions_recommandees) ? result.actions_recommandees : [result.actions_recommandees]).map((action, i) => (
                                <li key={`item-${i}`} className="flex items-start gap-2 text-green-700 bg-green-50 p-3 rounded-lg">
                                  <ClipboardList className="w-5 h-5 mt-0.5 flex-shrink-0" />
                                  <span>{action}</span>
                                </li>
                              ))}
                            </ul>
                          </div>
                        )}
                      </>
                    )}

                    {/* CCTP Result - Enhanced for domain-specific fields */}
                    {documentType === "cctp" && Object.keys(result).length > 0 && (
                      <>
                        {/* Header Info */}
                        {(result.id || result.titre_operation || result.titre_mission || result.titre_marche) && (
                          <div className="bg-emerald-50 p-4 rounded-xl border border-emerald-200">
                            {hasValue(result.id) && (
                              <Badge className="bg-emerald-100 text-emerald-700 mb-2">{result.id}</Badge>
                            )}
                            <h3 className="font-semibold text-slate-900 text-lg">
                              {result.titre_operation || result.titre_mission || result.titre_marche}
                            </h3>
                            {(result.maitre_ouvrage || result.acheteur) && (
                              <p className="text-sm text-slate-600 mt-1">{result.maitre_ouvrage || result.acheteur}</p>
                            )}
                          </div>
                        )}

                        {/* Location / Execution place */}
                        {(result.localisation_chantier || result.lieu_execution || result.lieu_livraison) && (
                          <div className="bg-slate-50 p-3 rounded-lg">
                            <span className="text-xs text-slate-500">Lieu d'exécution / Livraison</span>
                            <p className="font-medium text-slate-900">{result.localisation_chantier || result.lieu_execution || result.lieu_livraison}</p>
                          </div>
                        )}

                        {/* Nature / Category */}
                        {(result.nature_travaux || result.categorie_service || result.categorie_fournitures) && (
                          <div>
                            <h3 className="font-semibold text-slate-900 mb-2 flex items-center gap-2">
                              <div className="w-2 h-2 rounded-full bg-emerald-500" />
                              Nature / Catégorie
                            </h3>
                            <p className="text-slate-600 bg-emerald-50 p-3 rounded-lg border border-emerald-200">
                              {result.nature_travaux || result.categorie_service || result.categorie_fournitures}
                            </p>
                          </div>
                        )}

                        {/* Description technique / Objet mission / Description produits */}
                        {(result.description_technique || result.objet_mission || result.description_produits || result.objet_technique) && (
                          <div>
                            <h3 className="font-semibold text-slate-900 mb-2 flex items-center gap-2">
                              <div className="w-2 h-2 rounded-full bg-violet-500" />
                              Description technique
                            </h3>
                            <p className="text-slate-600 bg-slate-50 p-3 rounded-lg whitespace-pre-line">
                              {result.description_technique || result.objet_mission || result.description_produits || result.objet_technique}
                            </p>
                          </div>
                        )}

                        {/* Normes */}
                        {(result.normes_et_reglementations || result.normes_applicables || result.specifications_techniques || result.methodologie_imposee) && (
                          <div>
                            <h3 className="font-semibold text-slate-900 mb-2 flex items-center gap-2">
                              <div className="w-2 h-2 rounded-full bg-blue-500" />
                              Normes & Réglementations
                            </h3>
                            <p className="text-slate-600 bg-blue-50 p-3 rounded-lg border border-blue-200 whitespace-pre-line">
                              {result.normes_et_reglementations || result.normes_applicables || result.specifications_techniques || result.methodologie_imposee}
                            </p>
                          </div>
                        )}

                        {/* Matériaux / Moyens */}
                        {(result.materiaux_et_produits || result.materiaux_equipements || result.moyens_techniques) && (
                          <div>
                            <h3 className="font-semibold text-slate-900 mb-2 flex items-center gap-2">
                              <div className="w-2 h-2 rounded-full bg-amber-500" />
                              Matériaux / Moyens techniques
                            </h3>
                            <p className="text-slate-600 bg-amber-50 p-3 rounded-lg border border-amber-200 whitespace-pre-line">
                              {result.materiaux_et_produits || result.materiaux_equipements || result.moyens_techniques}
                            </p>
                          </div>
                        )}

                        {/* Contraintes / Conditions execution */}
                        {(result.contraintes_execution || result.conditions_execution || result.conditions_livraison) && (
                          <div>
                            <h3 className="font-semibold text-slate-900 mb-2 flex items-center gap-2">
                              <div className="w-2 h-2 rounded-full bg-orange-500" />
                              Contraintes d'exécution
                            </h3>
                            <p className="text-slate-600 bg-orange-50 p-3 rounded-lg border border-orange-200 whitespace-pre-line">
                              {result.contraintes_execution || result.conditions_execution || result.conditions_livraison}
                            </p>
                          </div>
                        )}

                        {/* Livrables */}
                        {(result.livrables_attendus || result.livrables || result.documents_a_produire) && (
                          <div>
                            <h3 className="font-semibold text-slate-900 mb-2 flex items-center gap-2">
                              <div className="w-2 h-2 rounded-full bg-blue-500" />
                              Livrables attendus
                            </h3>
                            <p className="text-slate-600 bg-slate-50 p-3 rounded-lg whitespace-pre-line">
                              {result.livrables_attendus || result.livrables || result.documents_a_produire}
                            </p>
                          </div>
                        )}

                        {/* Essais & Contrôles */}
                        {(result.essais_et_controles || result.essais_controles || result.echantillons_tests || result.controle_reception) && (
                          <div>
                            <h3 className="font-semibold text-slate-900 mb-2 flex items-center gap-2">
                              <div className="w-2 h-2 rounded-full bg-violet-500" />
                              Essais & Contrôles
                            </h3>
                            <p className="text-slate-600 bg-violet-50 p-3 rounded-lg border border-violet-200 whitespace-pre-line">
                              {result.essais_et_controles || result.essais_controles || result.echantillons_tests || result.controle_reception}
                            </p>
                          </div>
                        )}

                        {/* Qualifications / Certifications */}
                        {(result.qualifications_requises || result.certifications_requises) && (
                          <div className="bg-indigo-50 p-4 rounded-xl border border-indigo-200">
                            <h3 className="font-semibold text-indigo-700 mb-1">Qualifications / Certifications requises</h3>
                            <p className="text-indigo-800">{result.qualifications_requises || result.certifications_requises}</p>
                          </div>
                        )}

                        {/* Moyens humains (Services) */}
                        {hasValue(result.moyens_humains) && (
                          <div>
                            <h3 className="font-semibold text-slate-900 mb-2 flex items-center gap-2">
                              <div className="w-2 h-2 rounded-full bg-cyan-500" />
                              Moyens humains
                            </h3>
                            <p className="text-slate-600 bg-cyan-50 p-3 rounded-lg border border-cyan-200 whitespace-pre-line">
                              {result.moyens_humains}
                            </p>
                          </div>
                        )}

                        {/* KPIs / Indicateurs (Services) */}
                        {hasValue(result.indicateurs_performance) && (
                          <div>
                            <h3 className="font-semibold text-slate-900 mb-2 flex items-center gap-2">
                              <div className="w-2 h-2 rounded-full bg-teal-500" />
                              Indicateurs de performance (KPIs)
                            </h3>
                            <p className="text-slate-600 bg-teal-50 p-3 rounded-lg border border-teal-200 whitespace-pre-line">
                              {result.indicateurs_performance}
                            </p>
                          </div>
                        )}

                        {/* Quantités (Fournitures) */}
                        {hasValue(result.quantites_volumes) && (
                          <div>
                            <h3 className="font-semibold text-slate-900 mb-2 flex items-center gap-2">
                              <div className="w-2 h-2 rounded-full bg-purple-500" />
                              Quantités / Volumes
                            </h3>
                            <p className="text-slate-600 bg-purple-50 p-3 rounded-lg border border-purple-200">
                              {result.quantites_volumes}
                            </p>
                          </div>
                        )}

                        {/* Garanties / SAV */}
                        {(result.garanties_assurances || result.garantie_sav) && (
                          <div>
                            <h3 className="font-semibold text-slate-900 mb-2 flex items-center gap-2">
                              <div className="w-2 h-2 rounded-full bg-green-500" />
                              Garanties / SAV
                            </h3>
                            <p className="text-slate-600 bg-green-50 p-3 rounded-lg border border-green-200 whitespace-pre-line">
                              {result.garanties_assurances || result.garantie_sav}
                            </p>
                          </div>
                        )}

                        {/* Développement durable / Environnement */}
                        {(result.developpement_durable || result.exigences_environnementales) && (
                          <div>
                            <h3 className="font-semibold text-slate-900 mb-2 flex items-center gap-2">
                              <div className="w-2 h-2 rounded-full bg-green-600" />
                              Exigences environnementales
                            </h3>
                            <p className="text-slate-600 bg-green-50 p-3 rounded-lg border border-green-200">
                              {result.developpement_durable || result.exigences_environnementales}
                            </p>
                          </div>
                        )}

                        {/* Interfaces */}
                        {(result.interfaces_lots || result.interfaces_partenaires || result.interfaces_contrats) && (
                          <div>
                            <h3 className="font-semibold text-slate-900 mb-2 flex items-center gap-2">
                              <div className="w-2 h-2 rounded-full bg-slate-500" />
                              Interfaces / Coordination
                            </h3>
                            <p className="text-slate-600 bg-slate-50 p-3 rounded-lg whitespace-pre-line">
                              {result.interfaces_lots || result.interfaces_partenaires || result.interfaces_contrats}
                            </p>
                          </div>
                        )}

                        {/* Délais / Planning */}
                        {(result.delais_et_planning || result.decoupage_mission) && (
                          <div>
                            <h3 className="font-semibold text-slate-900 mb-2 flex items-center gap-2">
                              <div className="w-2 h-2 rounded-full bg-blue-500" />
                              Délais / Planning
                            </h3>
                            <p className="text-slate-600 bg-slate-50 p-3 rounded-lg whitespace-pre-line">
                              {result.delais_et_planning || result.decoupage_mission}
                            </p>
                          </div>
                        )}

                        {/* Points de vigilance */}
                        {(result.points_vigilance || result.points_attention) && (
                          <div className="bg-red-50 p-4 rounded-xl border border-red-200">
                            <h3 className="font-semibold text-red-700 mb-2 flex items-center gap-2">
                              <AlertTriangle className="w-4 h-4" />
                              Points de vigilance
                            </h3>
                            <p className="text-red-800 whitespace-pre-line">
                              {typeof (result.points_vigilance || result.points_attention) === 'string' 
                                ? (result.points_vigilance || result.points_attention)
                                : Array.isArray(result.points_vigilance || result.points_attention)
                                  ? (result.points_vigilance || result.points_attention).join('\n• ')
                                  : ''}
                            </p>
                          </div>
                        )}

                        {/* Complexité / Potentiel */}
                        {(result.potentiel_technique || result.complexite_mission || result.complexite_approvisionnement) && (
                          <div className="bg-violet-50 p-4 rounded-xl border border-violet-200">
                            <h3 className="font-semibold text-violet-700 mb-1">Évaluation de la complexité</h3>
                            <p className="text-violet-800">
                              {result.potentiel_technique || result.complexite_mission || result.complexite_approvisionnement}
                            </p>
                          </div>
                        )}

                        {/* Actions recommandées - fallback */}
                        {hasValue(result.actions_recommandees) && (Array.isArray(result.actions_recommandees) ? result.actions_recommandees.length > 0 : result.actions_recommandees) && (
                          <div>
                            <h3 className="font-semibold text-slate-900 mb-2 flex items-center gap-2">
                              <div className="w-2 h-2 rounded-full bg-green-500" />
                              Actions recommandées
                            </h3>
                            <ul className="space-y-2">
                              {(Array.isArray(result.actions_recommandees) ? result.actions_recommandees : [result.actions_recommandees]).map((action, i) => (
                                <li key={`item-${i}`} className="flex items-start gap-2 text-green-700 bg-green-50 p-3 rounded-lg">
                                  <ClipboardList className="w-5 h-5 mt-0.5 flex-shrink-0" />
                                  <span>{action}</span>
                                </li>
                              ))}
                            </ul>
                          </div>
                        )}
                      </>
                    )}

                    {/* MODE DÉTAILLÉ — Sections enrichies (extracted to ./synthese/DetailedSections.js) */}
                    {mode === "detaillee" && <DetailedSections result={result} />}

                    {/* Fallback for old format - resume/points_cles/alertes/actions */}
                    {!["rc", "ccap", "cctp"].includes(documentType) || (result.resume && !result.titre) ? (
                      <>
                        {/* Summary */}
                        {hasValue(result.resume) && (
                          <div>
                            <h3 className="font-semibold text-slate-900 mb-2 flex items-center gap-2">
                              <div className="w-2 h-2 rounded-full bg-violet-500" />
                              Résumé
                            </h3>
                            <p className="text-slate-600 leading-relaxed bg-slate-50 p-4 rounded-xl">
                              {result.resume}
                            </p>
                          </div>
                        )}

                        {/* Key Points */}
                        {result.points_cles?.length > 0 && (
                          <div>
                            <h3 className="font-semibold text-slate-900 mb-2 flex items-center gap-2">
                              <div className="w-2 h-2 rounded-full bg-green-500" />
                              Points clés
                            </h3>
                            <ul className="space-y-2">
                              {result.points_cles.map((point, i) => (
                                <li key={`item-${i}`} className="flex items-start gap-2 text-slate-600">
                                  <CheckCircle2 className="w-5 h-5 text-green-500 mt-0.5 flex-shrink-0" />
                                  <span>{point}</span>
                                </li>
                              ))}
                            </ul>
                          </div>
                        )}

                        {/* Alerts */}
                        {result.alertes?.length > 0 && (
                          <div>
                            <h3 className="font-semibold text-slate-900 mb-2 flex items-center gap-2">
                              <div className="w-2 h-2 rounded-full bg-amber-500" />
                              Points d'attention
                            </h3>
                            <ul className="space-y-2">
                              {result.alertes.map((alerte, i) => (
                                <li key={`item-${i}`} className="flex items-start gap-2 text-amber-700 bg-amber-50 p-3 rounded-lg">
                                  <AlertTriangle className="w-5 h-5 mt-0.5 flex-shrink-0" />
                                  <span>{alerte}</span>
                                </li>
                              ))}
                            </ul>
                          </div>
                        )}

                        {/* Actions */}
                        {result.actions?.length > 0 && (
                          <div>
                            <h3 className="font-semibold text-slate-900 mb-2 flex items-center gap-2">
                              <div className="w-2 h-2 rounded-full bg-blue-500" />
                              Actions recommandées
                            </h3>
                            <ul className="space-y-2">
                              {result.actions.map((action, i) => (
                                <li key={`item-${i}`} className="flex items-start gap-2 text-blue-700 bg-blue-50 p-3 rounded-lg">
                                  <ClipboardList className="w-5 h-5 mt-0.5 flex-shrink-0" />
                                  <span>{action}</span>
                                </li>
                              ))}
                            </ul>
                          </div>
                        )}
                      </>
                    ) : null}

                    {/* Reset Button + Download PDF (mode détaillé + plan payant) */}
                    {mode === "detaillee" && Object.keys(result).length > 0 && (
                      isPaidUser ? (
                        <Button
                          onClick={handleDownloadPdf}
                          disabled={generatingPdf}
                          className="w-full mt-4 bg-gradient-to-r from-violet-600 to-indigo-600 hover:from-violet-700 hover:to-indigo-700 text-white"
                          data-testid="download-pdf-btn"
                        >
                          {generatingPdf ? (
                            <>
                              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                              Génération du PDF…
                            </>
                          ) : (
                            <>
                              <Download className="w-4 h-4 mr-2" />
                              Télécharger la synthèse en PDF
                            </>
                          )}
                        </Button>
                      ) : (
                        <Button
                          onClick={() => navigate("/tarifs-synthese")}
                          variant="outline"
                          className="w-full mt-4 border-violet-300 text-violet-700 hover:bg-violet-50"
                          data-testid="upgrade-to-download-btn"
                        >
                          <Lock className="w-4 h-4 mr-2" />
                          Téléchargement PDF réservé aux abonnés - Voir les plans
                        </Button>
                      )
                    )}

                    <Button
                      onClick={resetForm}
                      variant="outline"
                      className="w-full mt-4"
                      data-testid="new-analysis-btn"
                    >
                      Nouvelle analyse
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>
          </motion.div>
        </div>
      </div>
    </DashboardLayout>
  );
}
